/*
 * Назначение: блок «Параметры генерации» на вкладке «Модели».
 * Функционал: настройка max_tokens и temperature по задачам вместо зашитых
 * в коде значений — основной чат сверху, служебные задачи (заявка, выжимка,
 * напоминание, тест) в свёрнутом блоке. Для служебных задач можно отметить
 * свои модели из списка проекта (провайдер берётся у модели): если ничего не
 * отмечено, задача работает на моделях проекта, как основной чат.
 * Плюс переключатель «не отправлять заявку без телефона».
 */

import { useState } from "react";
import { ChevronDown, ChevronUp, Loader2, Save } from "lucide-react";

import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import type { AiParams, AiTaskKey, ProjectDetails } from "../../../lib/types";

import { useAiProviders } from "./provider-select";
import { TaskModelsPicker } from "./task-models-picker";
import { useProjectMutation } from "./shared";

const TASK_LABELS: Record<AiTaskKey, string> = {
  chat: "Основной чат с клиентом",
  lead: "Разбор заявки (имя, услуга, суть)",
  summary: "Выжимка диалога для менеджера",
  reminder: "Текст возвратного напоминания",
  test: "Кнопка «Тест» в этой вкладке",
};

const SERVICE_TASKS: AiTaskKey[] = ["lead", "summary", "reminder", "test"];
const TASKS_WITH_MODELS: AiTaskKey[] = ["lead", "summary", "reminder"];

/** Одна строка настроек задачи: лимит, температура и (опционально) модели. */
function TaskRow({
  task,
  params,
  project,
  onChange,
}: {
  task: AiTaskKey;
  params: AiParams[AiTaskKey];
  project: ProjectDetails;
  onChange: (task: AiTaskKey, patch: Partial<AiParams[AiTaskKey]>) => void;
}) {
  const providers = useAiProviders();
  const withModels = TASKS_WITH_MODELS.includes(task);
  return (
    <div className="rounded-lg border border-border p-3">
      <p className="mb-2 text-sm font-medium">{TASK_LABELS[task]}</p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-muted-foreground">
          Лимит ответа (16–4000)
          <Input
            type="number"
            min={16}
            max={4000}
            className="mt-1 w-32"
            value={params.max_tokens}
            onChange={(e) => onChange(task, { max_tokens: Number(e.target.value) })}
          />
        </label>
        <label className="text-xs text-muted-foreground">
          Температура (0–2)
          <Input
            type="number"
            min={0}
            max={2}
            step={0.1}
            className="mt-1 w-28"
            value={params.temperature}
            onChange={(e) => onChange(task, { temperature: Number(e.target.value) })}
          />
        </label>
        {withModels && (
          <div className="min-w-[260px] flex-1">
            <TaskModelsPicker
              projectModels={project.models_config ?? []}
              providers={providers}
              selected={params.models ?? []}
              onChange={(models) => onChange(task, { models })}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export function GenerationParams({ project }: { project: ProjectDetails }) {
  const mutation = useProjectMutation("сохранить параметры генерации");
  const [params, setParams] = useState<AiParams>(project.ai_params);
  const [requirePhone, setRequirePhone] = useState<boolean>(project.lead_require_phone);
  const [duplicateNotice, setDuplicateNotice] = useState<string>(
    project.lead_duplicate_notice ?? "",
  );
  const [open, setOpen] = useState(false);

  const change = (task: AiTaskKey, patch: Partial<AiParams[AiTaskKey]>) =>
    setParams((prev) => ({ ...prev, [task]: { ...prev[task], ...patch } }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Параметры генерации</CardTitle>
        <CardDescription>
          Лимит ответа и «температура» (разброс формулировок: ниже — строже и
          предсказуемее, выше — свободнее). Значения по умолчанию подобраны под
          каждую задачу, менять стоит осознанно.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <TaskRow task="chat" params={params.chat} project={project} onChange={change} />

        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <div>
            <p className="text-sm font-medium">Заявку отправлять только с телефоном</p>
            <p className="text-xs text-muted-foreground">
              Пока клиент не оставил номер, заявка менеджеру не уходит: ИИ
              продолжает её собирать. Иначе менеджер получает письмо дважды —
              сначала с одной почтой, потом с телефоном.
            </p>
          </div>
          <Switch checked={requirePhone} onCheckedChange={setRequirePhone} />
        </div>

        <div className="rounded-lg border border-border p-3">
          <p className="text-sm font-medium">Повторная заявка в тот же день</p>
          <p className="mb-2 text-xs text-muted-foreground">
            Если клиент с тем же телефоном оформляет заявку повторно, а прежняя
            ещё в работе у менеджера, новая заявка не создаётся — ИИ отвечает
            этим текстом. На следующий день обращение оформляется как новая
            заявка. Пусто — используется формулировка по умолчанию.
          </p>
          <Textarea
            rows={2}
            placeholder="Ваша заявка уже в работе у менеджера — он свяжется с вами и всё уточнит."
            value={duplicateNotice}
            onChange={(e) => setDuplicateNotice(e.target.value)}
          />
        </div>

        <Button variant="outline" size="sm" onClick={() => setOpen((v) => !v)}>
          {open ? (
            <ChevronUp className="mr-2 h-4 w-4" />
          ) : (
            <ChevronDown className="mr-2 h-4 w-4" />
          )}
          Служебные задачи ИИ
        </Button>

        {open && (
          <div className="space-y-3">
            {SERVICE_TASKS.map((task) => (
              <TaskRow
                key={task}
                task={task}
                params={params[task]}
                project={project}
                onChange={change}
              />
            ))}
          </div>
        )}

        <div>
          <Button
            onClick={() =>
              mutation.mutate({
                ai_params: params,
                lead_require_phone: requirePhone,
                lead_duplicate_notice: duplicateNotice.trim() || null,
              })
            }
            disabled={mutation.isPending}
          >
            {mutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Сохранить параметры
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
